import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import ChevronLeft from "@mui/icons-material/ChevronLeft";
import ChevronRight from "@mui/icons-material/ChevronRight";
import { TestimonialCard, TestimonialCardProps } from "../TestimonialCard/TestimonialCard";
import globalStyles from "../../constants/globalStyles/theme.json";

const { colors, radius, spacing, breakpoints } = globalStyles;

interface CarouselProps {
  testimonials: TestimonialCardProps[];
}

const TABLET_QUERY = `(min-width: ${breakpoints.m})`;
const SWIPE_THRESHOLD = 50;

const getCardsPerPage = () => {
  if (typeof window === "undefined") {
    return 1;
  }

  return window.matchMedia(TABLET_QUERY).matches ? 2 : 1;
};

const Carousel: React.FC<CarouselProps> = ({ testimonials }) => {
  const [activePage, setActivePage] = useState(0);
  const [cardsPerPage, setCardsPerPage] = useState(getCardsPerPage);
  const pointerStartX = useRef<number | null>(null);

  useEffect(() => {
    const mediaQuery = window.matchMedia(TABLET_QUERY);
    const handleResize = () => setCardsPerPage(mediaQuery.matches ? 2 : 1);

    handleResize();
    mediaQuery.addEventListener("change", handleResize);

    return () => mediaQuery.removeEventListener("change", handleResize);
  }, []);

  const pages = useMemo(() => {
    return testimonials.reduce<TestimonialCardProps[][]>((result, testimonial, index) => {
      const pageIndex = Math.floor(index / cardsPerPage);
      result[pageIndex] = [...(result[pageIndex] ?? []), testimonial];
      return result;
    }, []);
  }, [cardsPerPage, testimonials]);

  useEffect(() => {
    setActivePage((currentPage) => Math.min(currentPage, Math.max(pages.length - 1, 0)));
  }, [pages.length]);

  const hasMultiplePages = pages.length > 1;

  const goToPage = (pageIndex: number) => {
    if (!hasMultiplePages) {
      return;
    }

    setActivePage((pageIndex + pages.length) % pages.length);
  };

  const goToPreviousPage = () => goToPage(activePage - 1);
  const goToNextPage = () => goToPage(activePage + 1);

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    pointerStartX.current = event.clientX;
  };

  const handlePointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    if (pointerStartX.current === null) {
      return;
    }

    const distance = event.clientX - pointerStartX.current;
    pointerStartX.current = null;

    if (Math.abs(distance) < SWIPE_THRESHOLD) {
      return;
    }

    if (distance > 0) {
      goToPreviousPage();
      return;
    }

    goToNextPage();
  };

  return (
    <CarouselContainer aria-label="Kundeomtaler">
      <CarouselFrame>
        {hasMultiplePages && (
          <NavButton type="button" onClick={goToPreviousPage} aria-label="Forrige omtale">
            <ChevronLeft aria-hidden="true" />
          </NavButton>
        )}

        <Viewport
          onPointerDown={handlePointerDown}
          onPointerUp={handlePointerUp}
          onPointerLeave={() => (pointerStartX.current = null)}>
          <Track $activePage={activePage}>
            {pages.map((page, pageIndex) => (
              <Page key={pageIndex} $cardsPerPage={cardsPerPage}>
                {page.map((testimonial) => (
                  <TestimonialCard key={testimonial.customerName} {...testimonial} />
                ))}
              </Page>
            ))}
          </Track>
        </Viewport>

        {hasMultiplePages && (
          <NavButton type="button" onClick={goToNextPage} aria-label="Neste omtale">
            <ChevronRight aria-hidden="true" />
          </NavButton>
        )}
      </CarouselFrame>

      {hasMultiplePages && (
        <Dots aria-label="Velg omtaleside">
          {pages.map((_, pageIndex) => (
            <Dot
              key={pageIndex}
              type="button"
              $active={pageIndex === activePage}
              aria-label={`Vis omtaleside ${pageIndex + 1}`}
              aria-current={pageIndex === activePage ? "true" : undefined}
              onClick={() => goToPage(pageIndex)}
            />
          ))}
        </Dots>
      )}
    </CarouselContainer>
  );
};

const CarouselContainer = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${spacing.space24};
  width: 100%;
  max-width: 1120px;
  margin: 0 auto;
  padding: 0 ${spacing.space16};
  box-sizing: border-box;
`;

const CarouselFrame = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  align-items: center;
  gap: ${spacing.space16};
  width: 100%;

  @media (min-width: ${breakpoints.m}) {
    grid-template-columns: 48px minmax(0, 1fr) 48px;
  }
`;

const Viewport = styled.div`
  overflow: hidden;
  width: 100%;
  touch-action: pan-y;
`;

const Track = styled.div<{ $activePage: number }>`
  display: flex;
  transform: translateX(-${({ $activePage }) => $activePage * 100}%);
  transition: transform 0.35s ease;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const Page = styled.div<{ $cardsPerPage: number }>`
  display: grid;
  grid-template-columns: repeat(${({ $cardsPerPage }) => $cardsPerPage}, minmax(0, 1fr));
  gap: ${spacing.space16};
  flex: 0 0 100%;
  min-width: 0;
  align-items: stretch;
  box-sizing: border-box;
  padding: 0 ${spacing.space8};

  @media (min-width: ${breakpoints.m}) {
    padding: 0;
  }
`;

const NavButton = styled.button`
  display: none;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border: 1px solid rgba(237, 234, 230, 0.35);
  border-radius: ${radius.rounded};
  background-color: rgba(237, 234, 230, 0.08);
  color: ${colors.primary.offWhite};
  cursor: pointer;
  transition:
    background-color 0.2s ease,
    border-color 0.2s ease,
    transform 0.2s ease;

  svg {
    width: 30px;
    height: 30px;
  }

  &:hover,
  &:focus-visible {
    background-color: ${colors.primary.offWhite};
    border-color: ${colors.primary.offWhite};
    color: ${colors.primary.green};
  }

  &:active {
    transform: scale(0.96);
  }

  @media (min-width: ${breakpoints.m}) {
    display: inline-flex;
  }
`;

const Dots = styled.div`
  display: flex;
  justify-content: center;
  gap: ${spacing.space8};
`;

const Dot = styled.button<{ $active: boolean }>`
  width: ${({ $active }) => ($active ? "28px" : "10px")};
  height: 10px;
  padding: 0;
  border: 0;
  border-radius: ${radius.rounded};
  background-color: ${({ $active }) => ($active ? colors.primary.offWhite : "rgba(237, 234, 230, 0.42)")};
  cursor: pointer;
  transition:
    width 0.2s ease,
    background-color 0.2s ease;

  &:focus-visible {
    outline: 2px solid ${colors.primary.offWhite};
    outline-offset: 4px;
  }
`;

export default Carousel;
